import { Link } from "react-router-dom";
import { Wallet, ShieldCheck, HandHeart, ArrowRight } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";

/**
 * WarmPricing — answers "what does it cost?" before families bounce. Many AFH sites
 * (and both reference competitors) hide cost behind "varies," which forces a phone
 * call just to learn the basics and quietly loses anxious families. We don't publish
 * a fabricated number; instead we explain the payment PATHS honestly and promise a
 * real quote at the tour — which is the truthful, conversion-positive answer.
 */
const PATHS = [
  {
    icon: Wallet,
    title: "Private Pay",
    body: "Paid from savings, retirement income, or family contributions. Your monthly rate depends on the level of care your loved one needs — we'll put it in writing.",
  },
  {
    icon: ShieldCheck,
    title: "Long-Term Care Insurance",
    body: "Many policies cover adult family home care. Bring the policy and we'll help you read the daily benefit, waiting period, and what triggers coverage.",
  },
  {
    icon: HandHeart,
    title: "Medicaid",
    body: "Washington's Medicaid program can help cover care for residents who qualify. Ask us directly — we'll tell you plainly whether we can accept it for your situation.",
  },
];

export function WarmPricing() {
  const { isSeattle } = useLocation();
  const basePath = isSeattle ? "/seattle" : "";

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center">
          <span className="eyebrow text-accent-strong">Cost &amp; Payment</span>
          <h2 className="mt-4 display-md font-heading font-semibold tracking-[-0.025em] text-foreground">
            Straight answers on{" "}
            <span className="font-display-italic font-normal text-accent-strong">cost</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Cost is the question families are most afraid to ask first — so here it is up
            front. There are three common ways to pay, and most families use a mix over time.
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-3">
          {PATHS.map((path) => (
            <div
              key={path.title}
              className="rounded-3xl border border-border/50 bg-card p-7 shadow-[var(--shadow-card)]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
                <path.icon className="h-6 w-6 text-accent-strong" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">{path.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{path.body}</p>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-10 max-w-2xl rounded-2xl bg-muted/40 p-6 text-center">
          <p className="text-sm leading-relaxed text-foreground">
            The fastest way to a real number is a short conversation about your loved one's
            actual needs. We'll give you a clear, written quote at your tour — no guessing,
            no pressure.
          </p>
          <Link
            to={`${basePath}/contact#tour-form`}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 transition-colors hover:text-accent-strong hover:underline"
          >
            Get your quote at a tour
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
